import * as Api from '@ViewModels';
import { useState } from 'react';
import { useBaseStepState } from './useBaseStepState';

interface UseEmailLikeStepStateProps {
	step: (Api.IEmailAutomationStep & Api.IHtmlNewsletterAutomationStep) | null;
	template: Api.IAutomationTemplate | null;
	stepId: string;
	readonly: boolean;
	onSaveSuccess?: () => void;
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
	/**
	 * Determines whether advanced options should be initially expanded.
	 * Defaults to a check that covers both Email and HtmlNewsletter fields.
	 */
	hasAdvancedOptions?: (step: Api.IAutomationStep) => boolean;
}

interface UseEmailLikeStepStateResult {
	name: string;
	setName: (name: string) => void;
	tagToAddOnClick: string;
	setTagToAddOnClick: (tag: string) => void;
	showEmailLinkClickTags: boolean;
	setShowEmailLinkClickTags: (show: boolean) => void;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	setSkipStepContactFilter: (filter: Api.IContactsFilterRequest | null | undefined) => void;
	advancedOptionsToggled: boolean;
	setAdvancedOptionsToggled: (toggled: boolean) => void;
	showRequiredFieldError: boolean;
	setShowRequiredFieldError: (show: boolean) => void;
	autoSave: ReturnType<typeof useBaseStepState>['autoSave'];
	clearInlineErrorMessage: () => void;
}

/**
 * Shared state management hook for Email and HtmlNewsletter step renderers.
 *
 * Both step types share the same state shape: name, tagToAddOnClick,
 * showEmailLinkClickTags, contact filter, advanced options, and auto-save.
 * This hook consolidates that shared logic.
 */
export function useEmailLikeStepState({
	step,
	template,
	stepId,
	readonly,
	onSaveSuccess,
	onSaveError,
	hasAdvancedOptions,
}: UseEmailLikeStepStateProps): UseEmailLikeStepStateResult {
	const [name, setName] = useState(step?.name || '');
	const [tagToAddOnClick, setTagToAddOnClick] = useState(step?.tagToAddOnClick || '');
	const [showEmailLinkClickTags, setShowEmailLinkClickTags] = useState(!!step?.tagToAddOnClick);

	const defaultHasAdvancedOptions = (s: Api.IAutomationStep) => {
		const emailLikeStep = s as Api.IEmailAutomationStep & Api.IHtmlNewsletterAutomationStep;
		const hasSendOptionSelected = !!s.sendFromOptions && s.sendFromOptions.mode !== Api.SendEmailFrom.CurrentUser;
		if (emailLikeStep.tagToAddOnClick || emailLikeStep.options?.sendToSender) {
			return true;
		}
		return !!(s.contactFilter?.criteria && s.contactFilter.criteria.length > 0) || hasSendOptionSelected;
	};

	const baseState = useBaseStepState({
		step,
		template,
		stepId,
		readonly,
		onSaveSuccess,
		onSaveError,
		initialContactFilter: step?.contactFilter,
		hasAdvancedOptions: hasAdvancedOptions ?? defaultHasAdvancedOptions,
	});

	return {
		name,
		setName,
		tagToAddOnClick,
		setTagToAddOnClick,
		showEmailLinkClickTags,
		setShowEmailLinkClickTags,
		skipStepContactFilter: baseState.skipStepContactFilter,
		setSkipStepContactFilter: baseState.setSkipStepContactFilter,
		advancedOptionsToggled: baseState.advancedOptionsToggled,
		setAdvancedOptionsToggled: baseState.setAdvancedOptionsToggled,
		showRequiredFieldError: baseState.showRequiredFieldError,
		setShowRequiredFieldError: baseState.setShowRequiredFieldError,
		autoSave: baseState.autoSave,
		clearInlineErrorMessage: baseState.clearInlineErrorMessage,
	};
}
